import * as Sentry from "@sentry/nextjs";
import {
  Stack,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Code,
  Flex,
  Box,
  Spinner,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import { Display03, Paragraph02 } from "~/components/Texts";
import OnboardingNavigation from "~/components/onboarding/OnboardingNavigation";
import { useState, useEffect } from "react";
import useDebounce from "~/context/UseDebounce";
import { FiCheckCircle, FiX } from "react-icons/fi";
import { useTheme } from "@chakra-ui/react";
import { checkDomainAvailability } from "~/actions/ShopActions";
import { validateShopDomain } from "~/utils/InputValidators";
import { MIN_SHOP_DOMAIN_LENGTH } from "~/utils/Limits";

const REMOTE_VALIDATION_DELAY = 850;

export default function OnboardingDomain({
  onboardingData,
  onBack,
  onComplete,
  showBack,
}) {
  const chakraTheme = useTheme();
  const toast = useToast();
  const [domain, setDomain] = useState(
    onboardingData.domain ? onboardingData.domain : ""
  );
  const debouncedDomain = useDebounce(domain, REMOTE_VALIDATION_DELAY);
  const [isCheckingDomainAvailability, setCheckingDomainAvailability] =
    useState(false);
  const [isDomainAvailable, setDomainAvailable] = useState(null);

  const handleChange = (event) => {
    setDomainAvailable(null);
    setDomain(event.target.value);
  };

  const handleComplete = () => {
    onComplete({ domain: domain }, null);
  };

  useEffect(() => {
    if (debouncedDomain && validateShopDomain(debouncedDomain) === true) {
      setCheckingDomainAvailability(true);
      setDomainAvailable(null);

      checkDomainAvailability(domain)
        .then((isAvailable) => setDomainAvailable(isAvailable))
        .catch((error) => {
          Sentry.captureException(error);
          setDomainAvailable(false);
          showErrorToast();
        })
        .finally(() => setCheckingDomainAvailability(false));
    } else {
      setCheckingDomainAvailability(false);
      setDomainAvailable(null);
    }
  }, [debouncedDomain]);

  const isStepReady = () => {
    return (
      !isCheckingDomainAvailability &&
      isDomainAvailable === true &&
      validateShopDomain(debouncedDomain) === true
    );
  };

  function showErrorToast() {
    toast({
      position: "top",
      title: "Ocurrió un error.",
      description:
        "No pudimos validar la dirección en este momento. Inténtalo de nuevo en unos minutos.",
      status: "error",
      duration: 5000,
      isClosable: true,
    });
  }

  return (
    <Flex flex="1" direction="column">
      <Box flex="1">
        <Stack
          height="100%"
          width={{ mobile: "100%", desktop: "80%" }}
          spacing="8"
        >
          <Display03>Dirección web de tu tienda</Display03>
          <Paragraph02>
            La dirección web de tu tienda es la forma en la que tus clientes
            encontrarán a tu tienda a través de su navegador de internet.
          </Paragraph02>
          <Paragraph02>
            <b>Nota:</b> No puede contener espacios en blanco o ser el mismo que
            otra tienda actualmente en uso.
          </Paragraph02>
          <Paragraph02>
            Ejemplo: Para una tienda llamada “Super Sports” la dirección web de
            esa tienda podría ser{" "}
            <Code fontSize="16px" colorScheme="gray">
              www.shopstory.io/super-sports
            </Code>
          </Paragraph02>
          <FormControl
            isInvalid={
              validateShopDomain(domain) !== true &&
              domain.length >= MIN_SHOP_DOMAIN_LENGTH
            }
          >
            <InputGroup size="lg" width={{ mobile: "100%", desktop: "80%" }}>
              <InputLeftAddon
                children="www.shopstory.io/"
                border="none"
                backgroundColor={useColorModeValue(
                  "light.background.strong",
                  "dark.background.strong"
                )}
                textColor={useColorModeValue(
                  "light.brand.lite",
                  "dark.brand.lite"
                )}
              />
              <Input
                id="domain"
                value={domain}
                onChange={(event) => handleChange(event)}
                type="text"
                placeholder="mi-negocio"
              />
              {isCheckingDomainAvailability && (
                <InputRightAddon
                  children={
                    <Spinner
                      color={useColorModeValue(
                        chakraTheme.colors.light.brand.strong,
                        chakraTheme.colors.dark.brand.strong
                      )}
                    />
                  }
                  border="none"
                />
              )}
              {!isCheckingDomainAvailability && isDomainAvailable === true && (
                <InputRightAddon
                  children={
                    <FiCheckCircle color={chakraTheme.colors.feel.positive} />
                  }
                  border="none"
                />
              )}
              {!isCheckingDomainAvailability && isDomainAvailable === false && (
                <InputRightAddon
                  children={<FiX color={chakraTheme.colors.feel.negative} />}
                  border="none"
                />
              )}
            </InputGroup>
            <FormHelperText id="domain">
              Debe tener al menos {MIN_SHOP_DOMAIN_LENGTH} caracteres. Sólo
              minúsculas.
            </FormHelperText>
            <FormErrorMessage>{validateShopDomain(domain)}</FormErrorMessage>
          </FormControl>
        </Stack>
      </Box>
      <Box>
        <OnboardingNavigation
          showBack={showBack}
          readyToContinue={isStepReady()}
          onBack={() => onBack()}
          onNext={() => handleComplete()}
        />
      </Box>
    </Flex>
  );
}
